import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/apiService';

const STEP_ICONS: Record<string, string> = {
  'Booking':           '📄',
  'Document Complete': '✅',
  'Loan Login':        '🏦',
  'Verification':      '🔍',
  'Account Opening':   '🗂️',
  'Agreement':         '✍️',
  'Down Payment':      '💵',
  'Disbursement':      '💰',
  'RTO (Passing)':     '🚦',
  'Delivery':          '🚗',
};

const CustomerTracking: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [trackingData, setTrackingData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTracking = async () => {
      if (!token) return;
      try {
        setLoading(true);
        setError(null);
        const res = await apiService.getPublicTrackingData(token);
        if (res.success) {
          setTrackingData(res.data);
        } else {
          setError('We could not find tracking details for this token.');
        }
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Unable to load tracking details. Please double check the link.');
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
  }, [token]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Fetching your delivery progress...</p>
        </div>
      </div>
    );
  }

  if (error || !trackingData) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorTitle}>Tracking Not Found</h2>
          <p style={styles.errorMsg}>{error || 'Invalid or expired tracking token.'}</p>
          <p style={styles.errorHelp}>Please contact your sales agent if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  const { customer_name, phone, car_details, steps } = trackingData;

  const orderOfSteps = [
    'Booking', 'Document Complete', 'Loan Login', 'Verification',
    'Account Opening', 'Agreement', 'Down Payment', 'Disbursement',
    'RTO (Passing)', 'Delivery'
  ];

  // Map steps with raw status from db
  const mappedSteps = orderOfSteps.map(stepName => {
    const foundStep = (steps || []).find((s: any) => s.step_name === stepName);
    return {
      name: stepName,
      status: foundStep ? foundStep.status : 'Pending',
      reason: foundStep ? foundStep.reason : null,
      updated_at: foundStep ? foundStep.updated_at : null
    };
  });

  const completedCount = mappedSteps.filter(s => s.status === 'Done').length;
  const progressPercent = Math.round((completedCount / orderOfSteps.length) * 100);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.logo}>ASW CARS</h1>
        <p style={styles.tagline}>Live Vehicle Delivery Tracker</p>
      </div>

      <div style={styles.card}>
        {/* Customer & Car Info */}
        <div style={styles.infoGrid}>
          <div style={styles.infoCol}>
            <span style={styles.infoLabel}>CUSTOMER</span>
            <span style={styles.infoValue}>{customer_name}</span>
          </div>
          <div style={styles.infoCol}>
            <span style={styles.infoLabel}>CAR DETAILS</span>
            <span style={styles.infoValue}>🚗 {car_details || '—'}</span>
          </div>
          <div style={styles.infoCol}>
            <span style={styles.infoLabel}>PHONE</span>
            <span style={styles.infoValue}>{phone}</span>
          </div>
        </div>

        {/* Big progress bar */}
        <div style={styles.progressContainer}>
          <div style={styles.progressHeader}>
            <span>Overall Progress</span>
            <span style={styles.progressText}>{progressPercent}% Completed</span>
          </div>
          <div style={styles.progressBarTrack}>
            <div style={{ ...styles.progressBarFill, width: `${progressPercent}%` }} />
          </div>
        </div>

        {/* Timeline stepper */}
        <div style={styles.stepperContainer}>
          <h3 style={styles.stepperTitle}>Delivery Workflow Stages</h3>
          <div style={styles.stepperList}>
            {mappedSteps.map((step, idx) => {
              const isDone = step.status === 'Done';
              const isHold = step.status === 'Hold';
              const isCurrent = !isDone && !isHold && (idx === 0 || mappedSteps[idx - 1].status === 'Done');

              let badgeStyle = styles.stepBadgePending;
              let lineStyle = styles.stepLinePending;

              if (isDone) {
                badgeStyle = styles.stepBadgeDone;
                lineStyle = styles.stepLineDone;
              } else if (isHold) {
                badgeStyle = styles.stepBadgeHold;
                lineStyle = styles.stepLineHold;
              } else if (isCurrent) {
                badgeStyle = styles.stepBadgeCurrent;
              }

              return (
                <div key={step.name} style={styles.stepItem}>
                  {/* Left Column: Line & Circle */}
                  <div style={styles.stepIndicatorCol}>
                    <div style={{ ...styles.stepCircle, ...badgeStyle }}>
                      {STEP_ICONS[step.name] || '📋'}
                    </div>
                    {idx < mappedSteps.length - 1 && (
                      <div style={{ ...styles.stepLine, ...lineStyle }} />
                    )}
                  </div>

                  {/* Right Column: Step Info */}
                  <div style={styles.stepContentCol}>
                    <div style={styles.stepNameRow}>
                      <span style={{
                        ...styles.stepName,
                        fontWeight: isCurrent || isDone ? 700 : 500,
                        color: isDone ? '#065F46' : isHold ? '#991B1B' : isCurrent ? '#4F46E5' : '#64748B'
                      }}>
                        {step.name}
                      </span>
                      <span style={{
                        ...styles.statusText,
                        color: isDone ? '#10B981' : isHold ? '#EF4444' : isCurrent ? '#4F46E5' : '#94A3B8'
                      }}>
                        {isDone ? 'Completed' : isHold ? 'On Hold' : isCurrent ? 'In Progress' : 'Pending'}
                      </span>
                    </div>

                    {step.reason && (isHold || step.status === 'Pending') && (
                      <div style={styles.stepReasonCard}>
                        <span style={styles.reasonLabel}>Status Update:</span>
                        <p style={styles.reasonText}>{step.reason}</p>
                      </div>
                    )}

                    {step.updated_at && (
                      <span style={styles.stepDate}>
                        Updated {new Date(step.updated_at).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={styles.footerNote}>
          🛡️ This tracking page is encrypted and secure. Car delivery times are estimated.
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F7FA 100%)',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    boxSizing: 'border-box'
  },
  header: {
    textAlign: 'center',
    marginBottom: '28px'
  },
  logo: {
    fontSize: '28px',
    fontWeight: 900,
    color: '#1E1B4B',
    margin: '0 0 4px',
    letterSpacing: '1px'
  },
  tagline: {
    fontSize: '14px',
    color: '#4F46E5',
    fontWeight: 600,
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 16px -8px rgba(0,0,0,0.05)',
    border: '1px solid #E2E8F0',
    width: '100%',
    maxWidth: '640px',
    padding: '32px',
    boxSizing: 'border-box'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #E2E8F0',
    borderTopColor: '#4F46E5',
    borderRadius: '50%',
    animation: 'crm-spin 0.8s linear infinite',
    margin: '40px auto 20px'
  },
  loadingText: {
    textAlign: 'center',
    color: '#64748B',
    fontSize: '14.5px'
  },
  errorIcon: {
    fontSize: '48px',
    textAlign: 'center',
    marginBottom: '16px'
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1E293B',
    textAlign: 'center',
    margin: '0 0 8px'
  },
  errorMsg: {
    color: '#EF4444',
    fontSize: '14px',
    textAlign: 'center',
    margin: '0 0 16px'
  },
  errorHelp: {
    color: '#64748B',
    fontSize: '13px',
    textAlign: 'center',
    margin: 0
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '20px',
    borderBottom: '1px solid #F1F5F9',
    paddingBottom: '24px',
    marginBottom: '24px'
  },
  infoCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  infoLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#94A3B8',
    letterSpacing: '0.5px'
  },
  infoValue: {
    fontSize: '14.5px',
    fontWeight: 700,
    color: '#1E293B'
  },
  progressContainer: {
    background: '#F8FAFC',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '32px',
    border: '1px solid #F1F5F9'
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13.5px',
    fontWeight: 600,
    color: '#475569',
    marginBottom: '8px'
  },
  progressText: {
    color: '#4F46E5',
    fontWeight: 700
  },
  progressBarTrack: {
    height: '6px',
    background: '#E2E8F0',
    borderRadius: '3px',
    overflow: 'hidden'
  },
  progressBarFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #4F46E5, #10B981)',
    borderRadius: '3px',
    transition: 'width 0.5s ease'
  },
  stepperContainer: {
    marginBottom: '24px'
  },
  stepperTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1E293B',
    marginBottom: '20px'
  },
  stepperList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0'
  },
  stepItem: {
    display: 'flex',
    gap: '20px',
    minHeight: '80px'
  },
  stepIndicatorCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0
  },
  stepCircle: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    transition: 'all 0.25s',
    zIndex: 2
  },
  stepLine: {
    width: '3px',
    flexGrow: 1,
    margin: '4px 0',
    borderRadius: '1.5px',
    minHeight: '40px'
  },
  stepBadgeDone: { background: '#D1FAE5', color: '#065F46', border: '2px solid #10B981' },
  stepBadgeHold: { background: '#FEE2E2', color: '#991B1B', border: '2px solid #EF4444' },
  stepBadgeCurrent: { background: '#EEF2FF', color: '#4F46E5', border: '2px solid #4F46E5', boxShadow: '0 0 0 4px rgba(79,70,229,0.12)' },
  stepBadgePending: { background: '#F1F5F9', color: '#94A3B8', border: '2px solid #E2E8F0' },
  stepLineDone: { background: '#10B981' },
  stepLineHold: { background: '#EF4444' },
  stepLinePending: { background: '#E2E8F0' },
  stepContentCol: {
    flexGrow: 1,
    paddingTop: '6px',
    paddingBottom: '20px'
  },
  stepNameRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px'
  },
  stepName: {
    fontSize: '14.5px',
  },
  statusText: {
    fontSize: '12px',
    fontWeight: 600
  },
  stepReasonCard: {
    background: '#FFFBEB',
    border: '1px solid #FDE68A',
    borderRadius: '8px',
    padding: '8px 12px',
    marginTop: '6px',
    marginBottom: '6px'
  },
  reasonLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#B45309',
    display: 'block',
    marginBottom: '2px'
  },
  reasonText: {
    fontSize: '12.5px',
    color: '#92400E',
    margin: 0,
    lineHeight: 1.4
  },
  stepDate: {
    fontSize: '11px',
    color: '#94A3B8',
    display: 'block',
    marginTop: '2px'
  },
  footerNote: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#94A3B8',
    borderTop: '1px solid #F1F5F9',
    paddingTop: '20px',
    marginTop: '20px'
  }
};

export default CustomerTracking;
